// Client-side form validation for `novalidate` forms.
//
// Listens on <html> (document.documentElement) so it runs BEFORE the reCAPTCHA
// submit handler (which listens on `document`) — a submit event bubbles
// form → … → html → document, so an invalid form can `stopImmediatePropagation`
// to gate reCAPTCHA + the native/ASwap submit.
//
// Beyond per-field `required` / email checks it enforces checkbox GROUPS: any
// container marked `data-checkbox-group-required` must have ≥1 checkbox checked
// (used for the newsletter mailing-list selection).

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const EMAIL_RE = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;

const ERROR_CLASS = 'formfield-error';
const INVALID_CLASS = 'is-invalid';

const MESSAGES = {
  required: 'To pole jest wymagane.',
  email: 'Podaj poprawny adres e-mail.',
  checkbox: 'Zaznaczenie tego pola jest wymagane.',
  group: 'Wybierz przynajmniej jedną opcję.',
} as const;

const dataMsg = (el: HTMLElement, key: string, fallback: string): string =>
  el.dataset[key] || fallback;

const validRequired = (value: string): boolean => value.trim().length > 0;
const validRequiredEmail = (value: string): boolean => EMAIL_RE.test(value.trim());
const validEmail = (value: string): boolean =>
  value.trim().length === 0 ? true : validRequiredEmail(value);

const errorId = (field: FormField): string => {
  if (!field.id) field.id = `ff-${Math.random().toString(36).slice(2, 9)}`;
  return `${field.id}-error`;
};

const clearError = (host: HTMLElement, field?: FormField): void => {
  if (field) {
    field.removeAttribute('aria-invalid');
    field.classList.remove(INVALID_CLASS);
  }
  host.querySelector(`:scope > .${ERROR_CLASS}`)?.remove();
};

const setError = (host: HTMLElement, message: string, field?: FormField): void => {
  if (field) {
    field.setAttribute('aria-invalid', 'true');
    field.classList.add(INVALID_CLASS);
  }
  let el = host.querySelector<HTMLParagraphElement>(`:scope > .${ERROR_CLASS}`);
  if (!el) {
    el = document.createElement('p');
    el.className = ERROR_CLASS;
    if (field) el.id = errorId(field);
    host.appendChild(el);
  }
  el.textContent = message;
  if (field) {
    const id = el.id || errorId(field);
    el.id = id;
    const described = field.getAttribute('aria-describedby');
    if (!described || !described.split(/\s+/).includes(id)) {
      field.setAttribute('aria-describedby', described ? `${described} ${id}` : id);
    }
  }
};

const validateField = (field: FormField): true | string => {
  const type = field.getAttribute('type');
  const isRequired = field.hasAttribute('required');

  if (type === 'submit' || type === 'button' || type === 'hidden' || type === 'reset') {
    return true;
  }
  if (type === 'checkbox') {
    if (isRequired && !(field as HTMLInputElement).checked) {
      return dataMsg(field, 'fieldErrorRequired', MESSAGES.checkbox);
    }
    return true;
  }
  if (type === 'email') {
    if (isRequired && !validRequiredEmail(field.value)) {
      return validRequired(field.value)
        ? dataMsg(field, 'fieldErrorFormat', MESSAGES.email)
        : dataMsg(field, 'fieldErrorRequired', MESSAGES.required);
    }
    if (!validEmail(field.value)) return dataMsg(field, 'fieldErrorFormat', MESSAGES.email);
    return true;
  }
  if (isRequired && !validRequired(field.value)) {
    return dataMsg(field, 'fieldErrorRequired', MESSAGES.required);
  }
  return true;
};

const validateForm = (form: HTMLFormElement): boolean => {
  let firstInvalid: HTMLElement | null = null;

  // per-field
  form.querySelectorAll<FormField>('input, select, textarea').forEach((field) => {
    const host = field.closest<HTMLElement>('.formfield') || field.parentElement || form;
    clearError(host, field);
    const result = validateField(field);
    if (result !== true) {
      setError(host, result, field);
      if (!firstInvalid) firstInvalid = field;
    }
  });

  // checkbox groups — at least one checked
  form.querySelectorAll<HTMLElement>('[data-checkbox-group-required]').forEach((group) => {
    clearError(group);
    const checked = group.querySelector('input[type="checkbox"]:checked');
    if (!checked) {
      setError(group, dataMsg(group, 'groupError', MESSAGES.group));
      if (!firstInvalid) firstInvalid = group.querySelector('input[type="checkbox"]') || group;
    }
  });

  if (firstInvalid) {
    (firstInvalid as HTMLElement).focus?.();
    return false;
  }
  return true;
};

const onDocumentSubmit = (e: Event): void => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.hasAttribute('novalidate')) return;

  if (!validateForm(form)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    form.classList.add('was-validated');
    form.classList.remove('loading');
    // reset the Alpine `loading` flag so the submit label returns
    const data = (window as unknown as { Alpine?: { $data(el: Element): Record<string, unknown> } }).Alpine?.$data(form);
    if (data && 'loading' in data) data.loading = false;
  }
};

export const initFormValidate = (): void => {
  document.documentElement.addEventListener('submit', onDocumentSubmit);
};
